// frontend/src/pages/Cart.jsx
import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import CurrencyDisplay from "../components/UI/CurrencyDisplay";
import Button from "../components/UI/Button";
import Loader from "../components/UI/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faShoppingCart,
  faTractor,
  faArrowRight,
  faMinus,
  faPlus,
  faSeedling,
  faCalendarCheck,
  faBox,
  faTruck,
} from "@fortawesome/free-solid-svg-icons";
  import {faWhatsapp } from "@fortawesome/free-brands-svg-icons" 
// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// MAIN COMPONENT
// ============================================
const Cart = () => {
  const { cart, loading, updateItem, removeItem, clearCart } = useCart();
  const { user } = useAuth();
  const { convertPrice, formatPrice, currency } = useCurrency();

  const userType = user?.userType || "customer";
  const isFarmer = userType === "farmer";
  const totalQuantity =
    cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const isBulkCart = isFarmer && totalQuantity >= 100;

  const subtotalUSD =
    cart?.items?.reduce(
      (sum, item) => sum + (item.price?.usd || 0) * item.quantity,
      0,
    ) || 0;
  const discountUSD = cart?.summary?.discount?.usd || 0;
  const shippingUSD =
    cart?.summary?.shipping?.usd || (subtotalUSD >= 100 ? 0 : 10);
  const totalUSD = subtotalUSD - discountUSD + shippingUSD;

  // ============================================
  // RENDER
  // ============================================
  if (loading) return <Loader />;

  if (!cart?.items || cart.items.length === 0) {
    return (
      <motion.div
        className="cart-empty"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="empty-icon">🛒</div>
        <h2>Your Cart is Empty</h2>
        <p>
          Start adding products from our collection of premium livestock
          nutrition products sourced from Malaysia.
        </p>
        <div className="empty-actions">
          <Button href="/products" variant="primary" size="large">
            Continue Shopping
          </Button>
          <Button href="/preorder" variant="secondary" size="large">
            <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order GrowPro
          </Button>
          <a
            href="https://wa.me/23231030808"
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-empty"
          >
            <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Order
          </a>
        </div>
        <style>{`
          .cart-empty {
            text-align: center;
            padding: 4rem 1.5rem;
            max-width: 600px;
            margin: 0 auto;
          }
          .empty-icon {
            font-size: 4rem;
            margin-bottom: 1rem;
          }
          .cart-empty h2 {
            color: #006b2e;
            font-family: "Montserrat", sans-serif;
          }
          .cart-empty p {
            color: #5a5a5a;
            margin: 1rem 0 2rem;
          }
          .empty-actions {
            display: flex;
            gap: 1rem;
            justify-content: center;
            flex-wrap: wrap;
          }
          .whatsapp-empty {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem 1.5rem;
            background: #25d366;
            color: #ffffff;
            border-radius: 8px;
            text-decoration: none;
            font-weight: 600;
            font-size: 0.875rem;
            transition: all 0.3s ease;
          }
          .whatsapp-empty:hover {
            background: #1da851;
            transform: translateY(-2px);
          }
          @media (max-width: 480px) {
            .cart-empty {
              padding: 2rem 1rem;
            }
            .empty-actions {
              flex-direction: column;
              align-items: stretch;
            }
          }
        `}</style>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="cart-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="container cart-container">
        {/* Header */}
        <div className="cart-header">
          <h1>
            <FontAwesomeIcon icon={faShoppingCart} className="header-icon" />
            Shopping Cart
            <span className="cart-count">{cart.items.length} items</span>
          </h1>
          <div className="cart-header-actions">
            <Button href="/preorder" variant="secondary" size="small">
              <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order
            </Button>
            <button onClick={clearCart} className="clear-cart-btn">
              <FontAwesomeIcon icon={faTrash} /> Clear Cart
            </button>
          </div>
        </div>

        {/* Bulk Notice */}
        {isBulkCart && (
          <motion.div
            className="bulk-notice"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FontAwesomeIcon icon={faTractor} />
            <span>
              <strong>🌾 Bulk Order Detected!</strong> Your cart qualifies for
              bulk pricing. Proceed to checkout for special farmer rates.
            </span>
          </motion.div>
        )}

        {/* Cart Layout */}
        <div className="cart-layout">
          {/* Cart Items */}
          <div className="cart-items">
            {cart.items.map((item, index) => {
              const itemPrice = item.price?.usd || 0;
              const itemTotal = itemPrice * item.quantity;
              const itemDiscount = item.discountPercent || 0;
              const isGrowPro = item.slug === "kenali-growpro";

              return (
                <motion.div
                  key={item._id}
                  className={`cart-item ${isGrowPro ? "growpro" : ""}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  exit={{ opacity: 0, x: -20 }}
                  layout
                >
                  <div className="item-image">
                    {item.image || item.product?.images?.[0] ? (
                      <img
                        src={item.image || item.product?.images[0]}
                        alt={item.name}
                      />
                    ) : (
                      <div className="item-image-placeholder">📦</div>
                    )}
                    {isGrowPro && (
                      <span className="pre-order-badge">
                        <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order
                      </span>
                    )}
                  </div>

                  <div className="item-details">
                    <Link
                      to={`/products/${item.productSlug || item.product?.slug}`}
                      className="item-name"
                    >
                      {item.name}
                    </Link>
                    {itemDiscount > 0 && (
                      <div className="item-discount">
                        {itemDiscount}% bulk discount applied
                      </div>
                    )}
                    {isGrowPro && (
                      <div className="pre-order-available">
                        <FontAwesomeIcon icon={faCalendarCheck} /> Pre-order
                        available
                      </div>
                    )}
                    <div className="item-price">
                      {item.quantity} ×{" "}
                      <CurrencyDisplay amountUSD={itemPrice} compact />
                    </div>
                  </div>

                  <div className="item-total">
                    <CurrencyDisplay amountUSD={itemTotal} />
                  </div>

                  <div className="item-quantity">
                    <button
                      className="qty-btn"
                      onClick={() => {
                        if (item.quantity > 1) {
                          updateItem(item._id, item.quantity - 1);
                        }
                      }}
                      aria-label="Decrease quantity"
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                    <input
                      type="number"
                      min={isFarmer ? 25 : 1}
                      value={item.quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (val > 0) updateItem(item._id, val);
                      }}
                      className="qty-input"
                      aria-label="Quantity"
                    />
                    <button
                      className="qty-btn"
                      onClick={() => updateItem(item._id, item.quantity + 1)}
                      aria-label="Increase quantity"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>

                  <button
                    className="item-remove"
                    onClick={() => removeItem(item._id)}
                    aria-label="Remove item"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </motion.div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="cart-summary">
            <h3>Order Summary</h3>

            <div className="summary-row">
              <span className="label">Subtotal:</span>
              <span className="value">
                <CurrencyDisplay amountUSD={subtotalUSD} />
              </span>
            </div>

            {discountUSD > 0 && (
              <div className="summary-row discount">
                <span>Bulk Discount:</span>
                <span>
                  -<CurrencyDisplay amountUSD={discountUSD} compact />
                </span>
              </div>
            )}

            <div className="summary-row">
              <span className="label">Shipping:</span>
              <span className="value">
                {isBulkCart || subtotalUSD >= 100 ? (
                  <span style={{ color: "#16a34a" }}>Free</span>
                ) : (
                  <CurrencyDisplay amountUSD={shippingUSD} compact />
                )}
              </span>
            </div>

            <hr className="summary-divider" />

            <div className="summary-row total">
              <span>Total:</span>
              <span className="value">
                <CurrencyDisplay amountUSD={totalUSD} />
              </span>
            </div>

            {isFarmer && totalQuantity > 0 && totalQuantity < 100 && (
              <div className="bulk-notice-small">
                ⚠️ Add <strong>{100 - totalQuantity}kg</strong> more to qualify
                for farmer bulk pricing
              </div>
            )}

            <div className="summary-actions">
              <Button href="/checkout" variant="primary" fullWidth size="large">
                Proceed to Checkout <FontAwesomeIcon icon={faArrowRight} />
              </Button>
              <Button
                href="/preorder"
                variant="secondary"
                fullWidth
                size="medium"
              >
                <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order GrowPro
              </Button>
              <Button
                href="/products"
                variant="outline"
                fullWidth
                size="medium"
              >
                Continue Shopping
              </Button>
            </div>

            <div className="summary-footer">
              <small>
                {isFarmer
                  ? "🌾 Bulk orders get priority processing"
                  : "🛒 Free shipping on orders over $100"}
              </small>
              <div className="summary-supplier">
                <FontAwesomeIcon icon={faBox} />
                <span>Sourced from Mylasha, Malaysia</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .cart-page {
          padding: 2rem 0;
        }
        @media (min-width: 768px) {
          .cart-page {
            padding: 4rem 0;
          }
        }

        .cart-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        @media (max-width: 480px) {
          .cart-container {
            padding: 0 0.75rem;
          }
        }

        .cart-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .cart-header h1 {
          color: #006B2E;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.5rem;
          font-family: "Montserrat", sans-serif;
        }
        .header-icon {
          color: #D9A321;
        }
        .cart-count {
          font-size: 0.8rem;
          font-weight: 400;
          color: #5A5A5A;
          background: #F8F8F8;
          padding: 0.1rem 0.5rem;
          border-radius: 12px;
          margin-left: 0.25rem;
        }
        .cart-header-actions {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        .clear-cart-btn {
          background: none;
          border: none;
          color: #dc2626;
          cursor: pointer;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          transition: all 0.3s ease;
          font-family: "Inter", sans-serif;
          font-weight: 500;
        }
        .clear-cart-btn:hover {
          background: #fee2e2;
        }

        .bulk-notice {
          background: linear-gradient(135deg, #006B2E 0%, #014421 100%);
          color: #fff;
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          text-align: center;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        .bulk-notice svg {
          font-size: 1.25rem;
          color: #D9A321;
        }

        .cart-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 768px) {
          .cart-layout {
            grid-template-columns: 1fr 340px;
          }
        }

        .cart-item {
          display: flex;
          gap: 1rem;
          padding: 1rem;
          border-bottom: 1px solid #f0f0f0;
          align-items: center;
          flex-wrap: wrap;
          transition: background 0.2s ease;
          position: relative;
          border-radius: 8px;
        }
        .cart-item:hover {
          background: #F8F9FA;
        }
        .cart-item.growpro {
          border-left: 3px solid #D9A321;
        }

        .item-image {
          width: 70px;
          height: 70px;
          background: #F8F8F8;
          border-radius: 8px;
          overflow: hidden;
          flex-shrink: 0;
          position: relative;
        }
        @media (min-width: 768px) {
          .item-image {
            width: 80px;
            height: 80px;
          }
        }
        .item-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .item-image-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          color: #D9A321;
        }

        .pre-order-badge {
          position: absolute;
          top: 0.25rem;
          left: 0.25rem;
          background: #D9A321;
          color: #ffffff;
          padding: 0.1rem 0.4rem;
          border-radius: 10px;
          font-size: 0.45rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.15rem;
        }

        .item-details {
          flex: 1;
          min-width: 150px;
        }
        .item-name {
          color: #006B2E;
          font-weight: 600;
          text-decoration: none;
          font-size: 0.938rem;
        }
        .item-name:hover {
          text-decoration: underline;
        }
        .item-discount {
          font-size: 0.75rem;
          color: #16a34a;
        }
        .pre-order-available {
          font-size: 0.7rem;
          color: #d97706;
        }
        .item-price {
          font-size: 0.875rem;
          color: #5A5A5A;
        }

        .item-total {
          font-weight: 600;
          color: #D9A321;
          min-width: 80px;
          text-align: right;
        }

        .item-quantity {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .qty-btn {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: 1px solid #e5e5e5;
          background: #ffffff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          font-size: 0.75rem;
        }
        .qty-btn:hover {
          border-color: #D9A321;
          background: #F8F8F8;
        }
        .qty-btn:active {
          transform: scale(0.9);
        }
        .qty-input {
          width: 50px;
          padding: 0.4rem;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          text-align: center;
          font-size: 0.875rem;
        }
        .qty-input:focus {
          outline: none;
          border-color: #D9A321;
          box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
        }

        .item-remove {
          background: none;
          border: none;
          color: #dc2626;
          cursor: pointer;
          font-size: 1.1rem;
          padding: 0.5rem;
          border-radius: 50%;
          transition: all 0.2s ease;
        }
        .item-remove:hover {
          background: #fee2e2;
        }

        .cart-summary {
          background: #F8F9FA;
          padding: 1.5rem;
          border-radius: 12px;
          height: fit-content;
          position: sticky;
          top: 90px;
          border: 1px solid #e5e5e5;
        }
        .cart-summary h3 {
          color: #006B2E;
          margin-bottom: 1.5rem;
          font-family: "Montserrat", sans-serif;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.75rem;
          font-size: 0.95rem;
        }
        .summary-row .label {
          color: #5A5A5A;
        }
        .summary-row .value {
          font-weight: 500;
        }
        .summary-row.discount {
          color: #16a34a;
        }
        .summary-row.total {
          font-size: 1.1rem;
        }
        .summary-row.total .value {
          color: #D9A321;
          font-weight: 700;
        }

        .summary-divider {
          margin: 1rem 0;
          border: none;
          border-top: 1px solid #e5e5e5;
        }

        .bulk-notice-small {
          background: #fef3c7;
          padding: 0.75rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          font-size: 0.875rem;
          text-align: center;
          border: 1px solid #fde68a;
          color: #92400e;
        }

        .summary-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .summary-footer {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #e5e5e5;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }
        .summary-footer small {
          color: #6B7280;
          font-size: 0.75rem;
        }
        .summary-supplier {
          font-size: 0.65rem;
          color: #5A5A5A;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
        .summary-supplier svg {
          color: #D9A321;
        }

        @media (max-width: 576px) {
          .cart-item {
            padding: 0.75rem;
            gap: 0.5rem;
          }
          .item-image {
            width: 50px;
            height: 50px;
          }
          .item-details {
            min-width: 100px;
          }
          .item-name {
            font-size: 0.813rem;
          }
          .item-total {
            min-width: 60px;
            font-size: 0.875rem;
          }
          .qty-input {
            width: 40px;
            font-size: 0.75rem;
          }
          .qty-btn {
            width: 26px;
            height: 26px;
            font-size: 0.65rem;
          }
          .cart-summary {
            padding: 1rem;
          }
          .cart-header h1 {
            font-size: 1.25rem;
          }
          .cart-header-actions {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .cart-item {
            flex-direction: row;
            flex-wrap: wrap;
          }
          .item-total {
            text-align: left;
            width: 100%;
            padding-left: 3.5rem;
          }
          .item-quantity {
            margin-left: auto;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default Cart;